import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { saveAs } from 'file-saver';
import './HistoryPage.css';
import logo from './logoBranca.png';
import { FadeIn, AnimatedButton } from './components/magicui';
import { SearchIcon, DownloadIcon, RefreshIcon, FilterIcon, BackIcon, LogoutIcon, TrashIcon } from './components/PastelGlyphIcons';

/**
 * Página dedicada ao histórico de números de série
 */
function HistoryPage({ userRole, userRegionals, userId, onLogout }) {
    const navigate = useNavigate();

    // Estados principais
    const [history, setHistory] = useState([]);
    const [filteredHistory, setFilteredHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [totalCount, setTotalCount] = useState(0);

    // Estados de filtros
    const [filters, setFilters] = useState({
        searchTerm: '',
        regional: '',
        dateFrom: '',
        dateTo: '',
        user: '',
        sortBy: 'created_at',
        sortOrder: 'desc'
    });

    // Estados de paginação
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(50);

    // Estados de interface
    const [showFilters, setShowFilters] = useState(false);
    const [availableUsers, setAvailableUsers] = useState([]);

    /**
     * Busca todos os registros do histórico usando paginação para contornar limite do Supabase
     */
    const fetchAllHistory = useCallback(async () => {
        if (!userId) return;

        setLoading(true);
        setError('');

        try {
            let allData = [];
            let totalCount = 0;
            let pageSize = 1000; // Máximo por página do Supabase
            let currentPage = 0;
            let hasMore = true;

            console.log('Iniciando busca paginada...');

            while (hasMore) {
                let query = supabase
                    .from('serial_numbers')
                    .select(`
                        id,
                        serial_number,
                        created_at,
                        user_id,
                        profiles:user_id (
                            email
                        )
                    `, { count: 'exact' });

                // Aplicar filtros baseados no papel do usuário
                if (userRole === 'master') {
                    console.log('Usuário master: buscando todos os NS');
                    // Master pode ver todos os registros - não aplica nenhum filtro
                } else {
                    console.log('Usuário não-master: só pode ver NS que ele próprio gerou');
                    // Usuários não-master só podem ver os NS que eles próprios geraram
                    query = query.eq('user_id', userId);
                }

                // Ordenação e paginação
                query = query
                    .order(filters.sortBy, { ascending: filters.sortOrder === 'asc' })
                    .range(currentPage * pageSize, (currentPage + 1) * pageSize - 1);

                const { data, error, count } = await query;

                if (error) {
                    console.error('Erro ao buscar histórico:', error);
                    setError('Erro ao carregar o histórico de números de série.');
                    return;
                }

                // Na primeira página, guardar o total
                if (currentPage === 0) {
                    totalCount = count || 0;
                    console.log('Total de registros no banco:', totalCount);
                }

                // Adicionar dados da página atual
                if (data && data.length > 0) {
                    allData = [...allData, ...data];
                    console.log(`Página ${currentPage + 1}: ${data.length} registros carregados`);

                    // Verificar se há mais páginas
                    hasMore = data.length === pageSize;
                    currentPage++;
                } else {
                    hasMore = false;
                }

                // Limite de segurança para evitar loops infinitos
                if (currentPage > 50) { // Máximo 50.000 registros
                    console.warn('Limite de páginas atingido');
                    hasMore = false;
                }
            }

            console.log('Busca finalizada. Total carregado:', allData.length, 'de', totalCount);

            // Verificar anos únicos nos dados
            if (allData.length > 0) {
                const years = [...new Set(allData.map(item =>
                    item.serial_number.substring(0, 4)
                ))];
                console.log('Anos encontrados nos dados:', years);
            }

            setHistory(allData);
            setTotalCount(totalCount);
            setFilteredHistory(allData);

            // Extrair usuários únicos para filtro
            const users = [...new Set(allData.map(item => item.profiles?.email).filter(Boolean))];
            setAvailableUsers(users);

        } catch (err) {
            console.error('Erro inesperado:', err);
            setError('Erro inesperado ao carregar histórico.');
        } finally {
            setLoading(false);
        }
    }, [userId, userRole, filters.sortBy, filters.sortOrder]);

    /**
     * Aplica filtros nos dados
     */
    const applyFilters = useCallback(() => {
        let filtered = [...history];

        // Filtro por termo de busca (número de série)
        if (filters.searchTerm) {
            filtered = filtered.filter(item =>
                item.serial_number.toLowerCase().includes(filters.searchTerm.toLowerCase())
            );
        }

        // Filtro por regional
        if (filters.regional) {
            filtered = filtered.filter(item =>
                item.serial_number.includes(filters.regional)
            );
        }

        // Filtro por data
        if (filters.dateFrom) {
            const fromDate = new Date(filters.dateFrom);
            filtered = filtered.filter(item =>
                new Date(item.created_at) >= fromDate
            );
        }

        if (filters.dateTo) {
            const toDate = new Date(filters.dateTo);
            toDate.setHours(23, 59, 59, 999); // Fim do dia
            filtered = filtered.filter(item =>
                new Date(item.created_at) <= toDate
            );
        }

        // Filtro por usuário
        if (filters.user) {
            filtered = filtered.filter(item =>
                item.profiles?.email === filters.user
            );
        }

        setFilteredHistory(filtered);
        setCurrentPage(1); // Reset para primeira página
    }, [history, filters]);

    /**
     * Exporta os dados filtrados para CSV
     */
    const exportFilteredData = () => {
        if (!filteredHistory.length) {
            setError('Nenhum registro para exportar.');
            return;
        }

        try {
            const headers = ['Número de Série', 'Data de Criação', 'Usuário'];
            const csvData = filteredHistory.map(item => [
                item.serial_number,
                new Date(item.created_at).toLocaleString('pt-BR'),
                item.profiles?.email || 'N/A'
            ]);

            const csvContent = [headers, ...csvData]
                .map(row => row.map(field => `"${field}"`).join(','))
                .join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const fileName = `historico_ns_${filters.regional || 'todos'}_${new Date().toISOString().split('T')[0]}.csv`;
            saveAs(blob, fileName);

            setError(''); // Clear any previous errors
        } catch (error) {
            console.error('Erro ao exportar:', error);
            setError('Erro ao exportar histórico.');
        }
    };

    /**
     * Manipula mudanças nos filtros
     */
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    /**
     * Limpa todos os filtros
     */
    const clearFilters = () => {
        setFilters({
            searchTerm: '',
            regional: '',
            dateFrom: '',
            dateTo: '',
            user: '',
            sortBy: 'created_at',
            sortOrder: 'desc'
        });
    };

    // Efeitos
    useEffect(() => {
        fetchAllHistory();
    }, [fetchAllHistory]);

    useEffect(() => {
        applyFilters();
    }, [applyFilters]);

    // Cálculos de paginação
    const totalPages = Math.ceil(filteredHistory.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = filteredHistory.slice(startIndex, endIndex);

    // Lista de regionais para filtro
    const allRegionals = [
        { value: 'CR', label: 'CR - Caruaru' },
        { value: 'GR', label: 'GR - Garanhuns' },
        { value: 'PT', label: 'PT - Petrolina' },
        { value: 'ST', label: 'ST - Serra Talhada' },
        { value: 'AV', label: 'AV - Arcoverde' },
        { value: 'RF', label: 'RF - Recife' },
        { value: 'OR', label: 'OR - Ouricuri' },
    ];

    const availableRegionals = userRole === 'master' ? allRegionals : allRegionals.filter(regional => userRegionals.includes(regional.value));

    return (
        <div className="history-page">
            <div className="history-container">
                {/* Header Moderno */}
                <FadeIn delay={0} className="history-header">
                    <div className="header-content">
                        <div className="header-logo">
                            <img src={logo} alt="Logo" className="history-logo" />
                        </div>
                        <div className="header-text">
                            <h1>
                                Histórico de NS
                            </h1>
                            <FadeIn delay={1} className="p">
                                {userRole === 'master' ? 'Visualização Completa' : `Regionais: ${userRegionals.join(', ')}`}
                            </FadeIn>
                        </div>
                    </div>
                    <FadeIn delay={2} className="header-buttons">
                        <AnimatedButton
                            onClick={() => navigate('/postes')}
                            className="back-button"
                        >
                            <BackIcon size={16} style={{marginRight: '6px'}} />
                            Voltar ao Gerador
                        </AnimatedButton>
                        <AnimatedButton
                            onClick={onLogout}
                            className="logout-button"
                        >
                            <LogoutIcon size={16} style={{marginRight: '6px'}} />
                            Sair
                        </AnimatedButton>
                    </FadeIn>
                </FadeIn>

                {/* Barra de Controles */}
                <FadeIn delay={3} className="controls-bar">
                    <div className="stats-section">
                        <div className="stat-box">
                            <span className="stat-number">{filteredHistory.length}</span>
                            <span className="stat-label">Exibidos</span>
                        </div>
                        <div className="stat-box">
                            <span className="stat-number">{totalCount}</span>
                            <span className="stat-label">Total</span>
                        </div>
                        {(filters.searchTerm || filters.regional || filters.dateFrom || filters.dateTo || filters.user) && (
                            <div className="stat-box">
                                <span className="stat-icon">
                                    <SearchIcon size={24} />
                                </span>
                                <span className="stat-label">Filtrado</span>
                            </div>
                        )}
                    </div>

                    <div className="actions-section">
                        <AnimatedButton
                            onClick={() => setShowFilters(!showFilters)}
                            className={`btn ${showFilters ? 'btn-primary' : 'btn-secondary'}`}
                        >
                            <FilterIcon size={16} style={{marginRight: '6px'}} />
                             Filtros
                        </AnimatedButton>

                        <AnimatedButton
                            onClick={exportFilteredData}
                            className="btn btn-success"
                            disabled={!filteredHistory.length || loading}
                        >
                            <DownloadIcon size={16} style={{marginRight: '6px'}} />
                             Exportar
                        </AnimatedButton>

                        <AnimatedButton
                            onClick={fetchAllHistory}
                            className="btn btn-secondary"
                            disabled={loading}
                        >
                            <RefreshIcon size={16} style={{marginRight: '6px'}} />
                             Atualizar
                        </AnimatedButton>
                    </div>
                </FadeIn>

                {/* Painel de Filtros */}
                {showFilters && (
                    <div className="filters-panel">
                        <h3 className="filters-title">Filtros de Busca</h3>
                        <div className="filters-grid">
                            <div className="filter-field">
                                <label>Buscar por NS</label>
                                <input
                                    type="text"
                                    placeholder="Digite o número de série..."
                                    value={filters.searchTerm}
                                    onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                                    className="filter-input"
                                />
                            </div>

                            <div className="filter-field">
                                <label>Regional</label>
                                <select
                                    value={filters.regional}
                                    onChange={(e) => handleFilterChange('regional', e.target.value)}
                                    className="filter-select"
                                >
                                    <option value="">Todas as regionais</option>
                                    {availableRegionals.map(regional => (
                                        <option key={regional.value} value={regional.value}>
                                            {regional.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="filter-field">
                                <label>Data de início</label>
                                <input
                                    type="date"
                                    value={filters.dateFrom}
                                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                                    className="filter-input"
                                />
                            </div>

                            <div className="filter-field">
                                <label>Data final</label>
                                <input
                                    type="date"
                                    value={filters.dateTo}
                                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                                    className="filter-input"
                                />
                            </div>

                            <div className="filter-field">
                                <label>Usuário</label>
                                <select
                                    value={filters.user}
                                    onChange={(e) => handleFilterChange('user', e.target.value)}
                                    className="filter-select"
                                >
                                    <option value="">Todos os usuários</option>
                                    {availableUsers.map(user => (
                                        <option key={user} value={user}>
                                            {user}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="filter-field">
                                <label>Ordenação</label>
                                <div className="sort-field">
                                    <select
                                        value={filters.sortBy}
                                        onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                                        className="filter-select"
                                    >
                                        <option value="created_at">Data de Criação</option>
                                        <option value="serial_number">Número de Série</option>
                                    </select>
                                    <select
                                        value={filters.sortOrder}
                                        onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                                        className="filter-select"
                                    >
                                        <option value="desc">Decrescente</option>
                                        <option value="asc">Crescente</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="filters-actions">
                            <button onClick={clearFilters} className="btn btn-secondary">
                                <TrashIcon size={14} style={{marginRight: '6px'}} />
                                Limpar Filtros
                            </button>
                        </div>
                    </div>
                )}

                {/* Conteúdo Principal */}
                <div className="content-section">
                    {loading && (
                        <div className="loading-state">
                            <div className="loading-spinner"></div>
                            <p>Carregando histórico...</p>
                        </div>
                    )}

                    {error && (
                        <div className="error-state">
                            <p className="error-message">{error}</p>
                        </div>
                    )}

                    {!loading && !error && (
                        <>
                            {/* Tabela */}
                            <div className="table-wrapper">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Número de Série</th>
                                            <th>Data de Criação</th>
                                            <th>Usuário</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.map((item) => (
                                            <tr key={item.id}>
                                                <td className="serial-cell">{item.serial_number}</td>
                                                <td className="date-cell">{new Date(item.created_at).toLocaleString('pt-BR')}</td>
                                                <td className="user-cell">{item.profiles?.email || 'N/A'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                {filteredHistory.length === 0 && (
                                    <div className="empty-state">
                                        <p className="empty-message">Nenhum registro encontrado.</p>
                                    </div>
                                )}
                            </div>

                            {/* Paginação */}
                            {totalPages > 1 && (
                                <div className="pagination-bar">
                                    <div className="pagination-info">
                                        <span>
                                            Mostrando {startIndex + 1} a {Math.min(endIndex, filteredHistory.length)} de {filteredHistory.length} registros
                                        </span>

                                        <select
                                            value={itemsPerPage}
                                            onChange={(e) => {
                                                setItemsPerPage(Number(e.target.value));
                                                setCurrentPage(1);
                                            }}
                                            className="page-size-select"
                                        >
                                            <option value={25}>25 por página</option>
                                            <option value={50}>50 por página</option>
                                            <option value={100}>100 por página</option>
                                            <option value={200}>200 por página</option>
                                        </select>
                                    </div>

                                    <div className="pagination-controls">
                                        <button
                                            onClick={() => setCurrentPage(1)}
                                            disabled={currentPage === 1}
                                            className="page-btn"
                                        >
                                            ⏮️
                                        </button>

                                        <button
                                            onClick={() => setCurrentPage(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className="page-btn"
                                        >
                                            ◀️
                                        </button>

                                        <span className="page-info">
                                            Página {currentPage} de {totalPages}
                                        </span>

                                        <button
                                            onClick={() => setCurrentPage(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className="page-btn"
                                        >
                                            ▶️
                                        </button>

                                        <button
                                            onClick={() => setCurrentPage(totalPages)}
                                            disabled={currentPage === totalPages}
                                            className="page-btn"
                                        >
                                            ⏭️
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default HistoryPage;